import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Home, CalendarCheck, DollarSign, MessageSquare, Moon, BedDouble, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const fmtDate = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-black/5 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow duration-300">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs uppercase tracking-[0.15em] font-bold text-gray-400">{label}</span>
        <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: accent + "1a", color: accent }}>
          <Icon strokeWidth={1.5} size={18} />
        </div>
      </div>
      <p className="font-display text-4xl font-medium text-[#111827]">{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data));
  }, []);

  if (!stats) return <div className="flex justify-center pt-20"><Loader2 className="animate-spin text-[#1A365D]" size={28} /></div>;

  return (
    <div data-testid="dashboard-page" className="max-w-6xl">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500 mb-2">Overview</p>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none font-medium mb-10">Dashboard</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
        <StatCard testid="stat-properties" icon={Home} label="Properties" value={`${stats.active_properties}/${stats.total_properties}`} accent="#1A365D" />
        <StatCard testid="stat-checkins" icon={CalendarCheck} label="Upcoming Check-ins" value={stats.upcoming_checkins} accent="#81B29A" />
        <StatCard testid="stat-revenue" icon={DollarSign} label="Revenue (Month)" value={`$${stats.revenue_this_month.toLocaleString()}`} accent="#E07A5F" />
        <StatCard testid="stat-bookings" icon={BedDouble} label="Active Bookings" value={stats.total_bookings} accent="#1A365D" />
        <StatCard testid="stat-nights" icon={Moon} label="Booked Nights (Month)" value={stats.booked_nights_this_month} accent="#81B29A" />
        <StatCard testid="stat-inquiries" icon={MessageSquare} label="New Inquiries" value={stats.new_inquiries} accent="#E07A5F" />
      </div>

      <div className="bg-white border border-black/5 rounded-2xl p-6 shadow-sm" data-testid="upcoming-checkins-panel">
        <h2 className="font-display text-2xl font-medium mb-5">Upcoming Check-ins</h2>
        {stats.upcoming_list.length === 0 ? (
          <p className="text-gray-400 text-sm py-6 text-center">No check-ins in the next 30 days.</p>
        ) : (
          <div className="divide-y divide-black/5">
            {stats.upcoming_list.map((b) => (
              <div key={b.id} className="flex items-center justify-between py-3.5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#F4F1EA] text-[#1A365D] flex items-center justify-center font-semibold text-sm">
                    {b.guest_name.charAt(0).toUpperCase()}
                  </div>
                  <span className="font-medium text-gray-800">{b.guest_name}</span>
                </div>
                <span className="text-sm text-gray-500">{fmtDate(b.check_in)} → {fmtDate(b.check_out)}</span>
              </div>
            ))}
          </div>
        )}
        <button onClick={() => navigate("/admin/bookings")} data-testid="view-all-bookings" className="mt-5 text-sm font-medium text-[#1A365D] hover:underline">
          View all bookings →
        </button>
      </div>
    </div>
  );
}
